import { useState, useMemo, useEffect } from "react";
import { Layout } from "@/components/Layout";
import { PlanGate } from "@/components/PlanGate";
import { REQUIRES } from "@/lib/plans";
import { SUPPLEMENTS, GOALS, TIMING_SLOTS, INTRO, COMBINATIONS, FAQ, SAFETY } from "@/data/supplements";
import {
  MagnifyingGlass, X, Star, Scales, DownloadSimple, Lock,
  Warning, CheckCircle, Info, Clock, Barbell, Heart, ArrowsClockwise,
  Question, Shield, CircleNotch, Pill,
} from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { getLevel } from "@/lib/plans";
import { API } from "@/lib/api";
import { toast } from "sonner";

const FAV_KEY = "as_supplement_favs";

export default function SupplementsGuide() {
  const [query, setQuery] = useState("");
  const [goal, setGoal] = useState("all");
  const [active, setActive] = useState(null);
  const [favs, setFavs] = useState(() => {
    try { return JSON.parse(localStorage.getItem(FAV_KEY) || "[]"); } catch { return []; }
  });
  const [compareIds, setCompareIds] = useState([]);
  const [compareOpen, setCompareOpen] = useState(false);
  const [showFavs, setShowFavs] = useState(false);

  useEffect(() => {
    localStorage.setItem(FAV_KEY, JSON.stringify(favs));
  }, [favs]);

  const toggleFav = (id) => {
    setFavs((f) => (f.includes(id) ? f.filter((x) => x !== id) : [...f, id]));
  };
  const toggleCompare = (id) => {
    setCompareIds((c) => {
      if (c.includes(id)) return c.filter((x) => x !== id);
      if (c.length >= 3) {
        toast.info("Máximo 3 suplementos", { description: "Quita uno para agregar otro" });
        return c;
      }
      return [...c, id];
    });
  };

  const filtered = useMemo(() => {
    let list = SUPPLEMENTS;
    if (showFavs) list = list.filter((s) => favs.includes(s.id));
    if (goal !== "all") list = list.filter((s) => s.goals.includes(goal));
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter(
        (s) =>
          s.name.toLowerCase().includes(q) ||
          s.category.toLowerCase().includes(q) ||
          s.what.toLowerCase().includes(q)
      );
    }
    return list;
  }, [query, goal, favs, showFavs]);

  return (
    <Layout>
      <PlanGate required={REQUIRES.SUPPLEMENTS_GUIDE} planName="AS START">
      {/* HERO */}
      <section className="border-b border-as-border bg-as-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <p className="as-label text-as-green mb-3">GUÍA DE SUPLEMENTACIÓN</p>
          <h1 className="as-heading text-4xl sm:text-5xl lg:text-6xl text-white" data-testid="supplements-title">
            SUPLEMENTOS CON <span className="text-as-green">EVIDENCIA.</span>
          </h1>
          <p className="text-as-gray mt-4 max-w-2xl">
            Solo suplementos con respaldo científico. Qué son, cómo funcionan, cuándo tomarlos, dosis y para quién.
            Toda la información basada en evidencia actual.
          </p>
          <div className="flex flex-wrap gap-6 mt-6 text-xs text-as-gray">
            <span><span className="text-white font-bold">{SUPPLEMENTS.length}</span> suplementos</span>
            <span><span className="text-white font-bold">{GOALS.length}</span> objetivos</span>
            <span><span className="text-white font-bold">{FAQ.length}</span> preguntas frecuentes</span>
          </div>
          <div className="mt-6">
            <DownloadGuidePdfButton />
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="bg-as-surface py-12 border-b border-as-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-3 gap-8">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Info size={20} weight="duotone" color="#39FF14" />
              <h2 className="as-heading text-lg text-white">¿QUÉ ES UN SUPLEMENTO?</h2>
            </div>
            <p className="text-sm text-as-gray leading-relaxed">{INTRO.what_is}</p>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Warning size={20} weight="duotone" color="#39FF14" />
              <h2 className="as-heading text-lg text-white">MITOS Y REALIDADES</h2>
            </div>
            <ul className="space-y-3">
              {INTRO.myths.map((m, i) => (
                <li key={i} className="text-xs">
                  <p className="text-red-400 line-through mb-1">{m.m}</p>
                  <p className="text-white">→ {m.r}</p>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle size={20} weight="duotone" color="#39FF14" />
              <h2 className="as-heading text-lg text-white">PRIMERO LO PRIMERO</h2>
            </div>
            <ul className="space-y-2">
              {INTRO.priority.map((p, i) => (
                <li key={i} className="text-xs text-as-gray leading-relaxed">{p}</li>
              ))}
            </ul>
            <p className="text-xs text-as-green mt-3 font-bold">La suplementación viene DESPUÉS. No antes.</p>
          </div>
        </div>
      </section>

      {/* FILTERS */}
      <section className="bg-as-black sticky top-16 z-30 border-b border-as-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 space-y-3">
          <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center">
            <div className="relative flex-1">
              <MagnifyingGlass size={16} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-as-gray" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar suplemento (whey, creatina, cafeína...)"
                className="w-full bg-as-surface border border-as-border pl-9 pr-3 py-2 text-sm text-white placeholder:text-as-gray focus:border-as-green outline-none"
                data-testid="supplement-search"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowFavs((v) => !v)}
                className={`px-3 py-2 text-xs uppercase tracking-wider font-bold flex items-center gap-2 transition-colors ${
                  showFavs ? "bg-as-green text-as-black" : "bg-as-surface text-white border border-as-border hover:border-as-green"
                }`}
                data-testid="fav-toggle"
              >
                <Star size={14} weight={showFavs ? "fill" : "bold"} /> Favoritos ({favs.length})
              </button>
              <button
                onClick={() => setCompareOpen(true)}
                disabled={compareIds.length < 2}
                className="px-3 py-2 text-xs uppercase tracking-wider font-bold bg-as-surface text-white border border-as-border hover:border-as-green disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
                data-testid="compare-open"
              >
                <Scales size={14} weight="bold" /> Comparar ({compareIds.length})
              </button>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setGoal("all")}
              className={`px-3 py-1 text-[10px] uppercase tracking-wider font-bold transition-colors ${
                goal === "all" ? "bg-as-green text-as-black" : "bg-as-surface text-white border border-as-border hover:border-as-green"
              }`}
              data-testid="goal-all"
            >
              Todos
            </button>
            {GOALS.map((g) => (
              <button
                key={g.id}
                onClick={() => setGoal(g.id)}
                className={`px-3 py-1 text-[10px] uppercase tracking-wider font-bold transition-colors ${
                  goal === g.id ? "bg-as-green text-as-black" : "bg-as-surface text-white border border-as-border hover:border-as-green"
                }`}
                data-testid={`goal-${g.id}`}
              >
                {g.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* CARDS */}
      <section className="bg-as-surface py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((s) => (
              <SupplementCard
                key={s.id}
                s={s}
                onOpen={() => setActive(s)}
                isFav={favs.includes(s.id)}
                onToggleFav={() => toggleFav(s.id)}
                isComparing={compareIds.includes(s.id)}
                onToggleCompare={() => toggleCompare(s.id)}
              />
            ))}
          </div>
          {filtered.length === 0 && (
            <div className="text-center py-16 text-as-gray">
              <p>No se encontraron suplementos con ese criterio.</p>
            </div>
          )}
        </div>
      </section>

      {/* TIMING GRID */}
      <TimingSection />

      {/* COMBINATIONS */}
      <CombinationsSection />

      {/* FAQ */}
      <FaqSection />

      {/* SAFETY */}
      <SafetySection />

      {active && <SupplementModal s={active} onClose={() => setActive(null)} />}
      {compareOpen && (
        <CompareModal
          items={SUPPLEMENTS.filter((s) => compareIds.includes(s.id))}
          onClose={() => setCompareOpen(false)}
          onRemove={(id) => setCompareIds((c) => c.filter((x) => x !== id))}
        />
      )}
      </PlanGate>
    </Layout>
  );
}

function EvidenceBadge({ level }) {
  const map = {
    Alta: "bg-as-green text-as-black",
    "Media-Alta": "bg-emerald-500 text-white",
    Media: "bg-yellow-500 text-as-black",
    Baja: "bg-red-500 text-white",
  };
  return (
    <span className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 ${map[level] || map.Media}`}>
      Evidencia {level}
    </span>
  );
}

function SupplementCard({ s, onOpen, isFav, onToggleFav, isComparing, onToggleCompare }) {
  return (
    <div
      className={`bg-as-black border transition-colors cursor-pointer group ${
        isComparing ? "border-as-green" : "border-as-border hover:border-as-green"
      }`}
      data-testid={`supplement-${s.id}`}
    >
      <div className="p-5 border-b border-as-border" onClick={onOpen}>
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="w-10 h-10 rounded-full bg-as-surface flex items-center justify-center flex-shrink-0 border border-as-green/30">
            <Pill size={18} weight="duotone" color="#39FF14" />
          </div>
          <EvidenceBadge level={s.evidence} />
        </div>
        <p className="as-label text-as-green text-[10px] mb-1">{s.category.toUpperCase()}</p>
        <h3 className="as-heading text-lg text-white leading-tight">{s.name}</h3>
        <p className="text-xs text-as-gray mt-2 leading-relaxed line-clamp-3">{s.what}</p>
        <div className="flex flex-wrap gap-1 mt-3">
          {s.goals.slice(0, 3).map((g) => {
            const label = GOALS.find((x) => x.id === g)?.short || g;
            return (
              <span key={g} className="text-[9px] px-2 py-0.5 bg-as-surface text-as-gray border border-as-border">
                {label}
              </span>
            );
          })}
        </div>
      </div>
      <div className="p-3 flex items-center justify-between gap-2 text-[10px] uppercase tracking-wider font-bold">
        <button onClick={onToggleFav} className="flex items-center gap-1 text-as-gray hover:text-as-green" data-testid={`fav-${s.id}`}>
          <Star size={14} weight={isFav ? "fill" : "bold"} color={isFav ? "#39FF14" : undefined} />
          {isFav ? "Guardado" : "Favorito"}
        </button>
        <button onClick={onToggleCompare} className={`flex items-center gap-1 ${isComparing ? "text-as-green" : "text-as-gray hover:text-as-green"}`} data-testid={`compare-${s.id}`}>
          <Scales size={14} weight="bold" /> {isComparing ? "Comparando" : "Comparar"}
        </button>
        <button onClick={onOpen} className="text-as-green">
          Ver ficha →
        </button>
      </div>
    </div>
  );
}

function SupplementModal({ s, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto" onClick={onClose} data-testid="supplement-modal">
      <div className="bg-as-black border-2 border-as-green max-w-3xl w-full my-4 sm:my-0" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-as-border flex justify-between items-center sticky top-0 bg-as-black z-10">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1">
              <p className="as-label text-as-green text-[10px]">{s.category.toUpperCase()}</p>
              <EvidenceBadge level={s.evidence} />
            </div>
            <h2 className="as-heading text-lg sm:text-xl text-white">{s.name.toUpperCase()}</h2>
          </div>
          <button onClick={onClose} className="text-white hover:text-as-green ml-2">
            <X size={24} weight="bold" />
          </button>
        </div>
        <div className="p-4 sm:p-6 space-y-6">
          <ModalBlock icon={<Info size={18} weight="duotone" color="#39FF14" />} title="QUÉ ES">
            <p className="text-sm text-as-gray leading-relaxed">{s.what}</p>
          </ModalBlock>
          <ModalBlock icon={<Barbell size={18} weight="duotone" color="#39FF14" />} title="CÓMO FUNCIONA">
            <p className="text-sm text-as-gray leading-relaxed">{s.how_works}</p>
          </ModalBlock>
          <ModalBlock icon={<CheckCircle size={18} weight="duotone" color="#39FF14" />} title="BENEFICIOS">
            <ul className="space-y-1">
              {s.benefits.map((b, i) => (
                <li key={i} className="text-sm text-white leading-relaxed flex gap-2">
                  <CheckCircle size={14} weight="fill" color="#39FF14" className="flex-shrink-0 mt-0.5" /> {b}
                </li>
              ))}
            </ul>
          </ModalBlock>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-as-surface p-4 border-l-2 border-as-green">
              <div className="flex items-center gap-2 mb-2">
                <Clock size={16} weight="duotone" color="#39FF14" />
                <p className="as-label text-as-green text-[10px]">CUÁNDO TOMARLO</p>
              </div>
              <p className="text-xs text-white leading-relaxed">{s.when}</p>
            </div>
            <div className="bg-as-surface p-4 border-l-2 border-as-green">
              <div className="flex items-center gap-2 mb-2">
                <Pill size={16} weight="duotone" color="#39FF14" />
                <p className="as-label text-as-green text-[10px]">DOSIS RECOMENDADA</p>
              </div>
              <p className="text-xs text-white leading-relaxed">{s.dosage}</p>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-as-surface p-4 border-l-2" style={{ borderColor: "#FF3333" }}>
              <div className="flex items-center gap-2 mb-3">
                <Warning size={16} weight="duotone" color="#FF3333" />
                <p className="as-label text-[10px]" style={{ color: "#FF3333" }}>POSIBLES EFECTOS SECUNDARIOS</p>
              </div>
              <ul className="space-y-1.5">
                {s.side_effects.map((e, i) => (
                  <li key={i} className="text-xs text-white flex gap-2">
                    <X size={12} weight="bold" color="#FF3333" className="flex-shrink-0 mt-1" /> {e}
                  </li>
                ))}
              </ul>
            </div>
            <div className="grid grid-cols-1 gap-3">
              <div className="bg-as-surface p-3 border-l-2 border-as-green">
                <p className="as-label text-as-green text-[10px] mb-1">QUIÉN DEBERÍA USARLO</p>
                <ul className="text-xs text-white space-y-1">
                  {s.recommended_for.map((r, i) => <li key={i}>· {r}</li>)}
                </ul>
              </div>
              <div className="bg-as-surface p-3 border-l-2" style={{ borderColor: "#FF3333" }}>
                <p className="as-label text-[10px] mb-1" style={{ color: "#FF3333" }}>QUIÉN DEBERÍA EVITARLO</p>
                <ul className="text-xs text-white space-y-1">
                  {s.avoid_if.map((r, i) => <li key={i}>· {r}</li>)}
                </ul>
              </div>
            </div>
          </div>
          {s.evidence_note && (
            <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 text-xs text-yellow-300">
              <b>Nota sobre evidencia:</b> {s.evidence_note}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ModalBlock({ icon, title, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <h4 className="as-heading text-sm text-as-green">{title}</h4>
      </div>
      {children}
    </div>
  );
}

function CompareModal({ items, onClose, onRemove }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex items-start justify-center p-2 sm:p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-as-black border-2 border-as-green max-w-6xl w-full my-4" onClick={(e) => e.stopPropagation()} data-testid="compare-modal">
        <div className="p-4 border-b border-as-border flex justify-between items-center sticky top-0 bg-as-black z-10">
          <div>
            <p className="as-label text-as-green text-[10px] mb-1">COMPARADOR</p>
            <h2 className="as-heading text-lg text-white">{items.length} SUPLEMENTOS</h2>
          </div>
          <button onClick={onClose} className="text-white hover:text-as-green" data-testid="compare-close"><X size={24} weight="bold" /></button>
        </div>
        <div className="p-4 grid gap-3" style={{ gridTemplateColumns: `repeat(${items.length}, 1fr)` }}>
          {items.map((s) => (
            <div key={s.id} className="bg-as-surface border border-as-border p-4">
              <div className="flex justify-between items-start mb-2">
                <EvidenceBadge level={s.evidence} />
                <button onClick={() => onRemove(s.id)} className="text-as-gray hover:text-red-400"><X size={14} weight="bold" /></button>
              </div>
              <h3 className="as-heading text-base text-white mb-1">{s.name}</h3>
              <p className="text-[10px] text-as-green mb-4">{s.category.toUpperCase()}</p>
              <CompareField label="Dosis" value={s.dosage} />
              <CompareField label="Cuándo" value={s.when} />
              <CompareField label="Objetivos" value={s.goals.map((g) => GOALS.find((x) => x.id === g)?.short).join(", ")} />
              <CompareField label="Beneficios" value={s.benefits.join(" · ")} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function CompareField({ label, value }) {
  return (
    <div className="mb-2 pb-2 border-b border-as-border">
      <p className="as-label text-as-green text-[9px] mb-0.5">{label}</p>
      <p className="text-xs text-white leading-relaxed">{value}</p>
    </div>
  );
}

function TimingSection() {
  return (
    <section className="bg-as-black py-12 border-t border-as-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="as-label text-as-green mb-2">HORARIOS DE CONSUMO</p>
        <h2 className="as-heading text-2xl sm:text-3xl text-white mb-8">CUÁNDO TOMAR CADA UNO</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {TIMING_SLOTS.map((slot) => {
            const items = SUPPLEMENTS.filter((s) => s.timing.includes(slot.id));
            return (
              <div key={slot.id} className="bg-as-surface border border-as-border p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Clock size={16} weight="duotone" color="#39FF14" />
                  <p className="as-label text-as-green text-[10px]">{slot.label.toUpperCase()}</p>
                </div>
                <ul className="space-y-1.5">
                  {items.map((s) => (
                    <li key={s.id} className="text-xs text-white">· {s.name}</li>
                  ))}
                  {items.length === 0 && <li className="text-xs text-as-gray">—</li>}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function CombinationsSection() {
  return (
    <section className="bg-as-surface py-12 border-t border-as-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="as-label text-as-green mb-2">COMBINACIONES</p>
        <h2 className="as-heading text-2xl sm:text-3xl text-white mb-8">QUÉ COMBINAR (Y QUÉ NO)</h2>
        <div className="grid md:grid-cols-2 gap-4">
          {COMBINATIONS.map((c, i) => (
            <div key={i} className={`bg-as-black border-l-2 p-4 ${c.ok ? "border-as-green" : "border-red-500"}`}>
              <div className="flex items-center gap-2 mb-2">
                {c.ok ? <CheckCircle size={18} weight="duotone" color="#39FF14" /> : <Warning size={18} weight="duotone" color="#FF3333" />}
                <h4 className="text-white font-bold text-sm">{c.title}</h4>
              </div>
              <p className={`text-[10px] uppercase tracking-wider font-bold mb-1 ${c.ok ? "text-as-green" : "text-red-400"}`}>{c.verdict}</p>
              <p className="text-xs text-as-gray leading-relaxed">{c.why}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FaqSection() {
  const [open, setOpen] = useState(null);
  return (
    <section className="bg-as-black py-12 border-t border-as-border">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="as-label text-as-green mb-2">PREGUNTAS FRECUENTES</p>
        <h2 className="as-heading text-2xl sm:text-3xl text-white mb-8 flex items-center gap-2">
          <Question size={28} weight="duotone" color="#39FF14" /> DUDAS COMUNES
        </h2>
        <div className="space-y-2">
          {FAQ.map((f, i) => (
            <div key={i} className="bg-as-surface border border-as-border">
              <button
                onClick={() => setOpen(open === i ? null : i)}
                className="w-full text-left px-4 py-3 flex justify-between items-center gap-2 hover:bg-as-black transition-colors"
                data-testid={`faq-${i}`}
              >
                <span className="text-sm text-white font-bold">{f.q}</span>
                <span className="text-as-green flex-shrink-0">{open === i ? "−" : "+"}</span>
              </button>
              {open === i && (
                <div className="px-4 pb-4 pt-1 text-xs text-as-gray leading-relaxed border-t border-as-border">
                  {f.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function SafetySection() {
  return (
    <section className="bg-red-500/5 py-12 border-t-2 border-red-500/30">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-3 mb-6">
          <Shield size={32} weight="duotone" color="#FF3333" />
          <div>
            <p className="as-label text-red-400 mb-1">SEGURIDAD</p>
            <h2 className="as-heading text-2xl text-white">ADVERTENCIAS IMPORTANTES</h2>
          </div>
        </div>
        <ul className="space-y-2 bg-as-surface border border-red-500/30 p-5">
          {SAFETY.map((s, i) => (
            <li key={i} className="text-sm text-white flex gap-2 leading-relaxed">
              <Warning size={16} weight="fill" color="#FF3333" className="flex-shrink-0 mt-0.5" /> {s}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function DownloadGuidePdfButton() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const level = getLevel(user);
  const canDownload = level >= 2;

  const handleDownload = async () => {
    if (!canDownload) {
      toast.error("Función exclusiva ELITE / EVOLUTION", {
        description: user ? "Actualiza tu plan para descargar la guía completa" : "Inicia sesión con plan ELITE o EVOLUTION",
      });
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/supplements/pdf`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          intro: INTRO,
          supplements: SUPPLEMENTS,
          combinations: COMBINATIONS,
          faq: FAQ,
          safety: SAFETY,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: "Error" }));
        throw new Error(err.detail || "Error al generar PDF");
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "guia_suplementacion_AS_TRAINING.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Guía descargada");
    } catch (e) {
      toast.error(e.message || "No pudimos generar el PDF");
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      onClick={handleDownload}
      disabled={loading}
      className={`inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider font-bold transition-colors ${
        canDownload
          ? "bg-as-green text-as-black hover:bg-white"
          : "bg-as-surface text-as-gray border border-as-border hover:border-as-green"
      } disabled:opacity-60`}
      data-testid="pdf-download-guide"
    >
      {loading ? (
        <CircleNotch size={16} weight="bold" className="animate-spin" />
      ) : canDownload ? (
        <DownloadSimple size={16} weight="bold" />
      ) : (
        <Lock size={16} weight="bold" />
      )}
      {loading ? "Generando PDF..." : canDownload ? "Descargar guía completa (PDF)" : "PDF ELITE / EVOLUTION"}
    </button>
  );
}
